import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../useLang';

const FONT = "'Helvetica Neue', Arial, sans-serif";
export const CREATOR_SIDEBAR_WIDTH = 240;
export const CREATOR_BREAKPOINT = 880;

// Navigation de l'espace créateur — entièrement autonome (tout le CSS est
// injecté ici, aucune classe externe requise). Même système que la nouvelle
// AdminNavbar.jsx de l'espace management : sidebar fixe sur desktop, tiroir
// derrière un bouton hamburger sous 880px. Avant, ce composant reposait sur
// .w-sidenav / .w-lang-toggle (design-system.css) qui ne sont pas dans le
// CSS actuellement déployé — d'où le rendu non stylé et replié en 3 lignes.
export default function AdminNav({ username, role, avatarUrl, onLogout, activePath }) {
    const navigate = useNavigate();
    const { lang, setLang, t } = useLang();
    const [open, setOpen] = useState(false);
    const initials = username ? username.slice(0, 2).toUpperCase() : '?';

    const NAV_ITEMS = [
        { label: t('dashboard'), path: '/admin/dashboard' },
        { label: t('posts'), path: '/admin/posts' },
        { label: t('agreement'), path: '/admin/agreement' },
    ];

    const go = (path) => { navigate(path); setOpen(false); };

    return (
        <>
            <style>{`
                .cnav-topbar { display: none; }
                .cnav-sidebar {
                    position: fixed; top: 0; left: 0; bottom: 0;
                    width: ${CREATOR_SIDEBAR_WIDTH}px;
                    background: #0e1013;
                    color: #f4efe4;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    overflow-y: auto;
                    z-index: 300;
                    box-sizing: border-box;
                    font-family: ${FONT};
                }
                .cnav-backdrop { display: none; }

                .cnav-logo {
                    display: flex; align-items: center; gap: 10px;
                    padding: 26px 22px 22px; cursor: pointer;
                }
                .cnav-logo img { height: 26px; width: auto; display: block; flex-shrink: 0; }
                .cnav-logo span { font-size: 14px; font-weight: 700; letter-spacing: -0.01em; }

                .cnav-close { display: none; }

                .cnav-items { padding: 8px 12px; }
                .cnav-item {
                    display: block; width: 100%; text-align: left;
                    background: none; border: none; border-left: 2px solid transparent;
                    padding: 10px 10px; margin: 1px 0;
                    font-family: ${FONT}; font-size: 13.5px; font-weight: 500;
                    color: rgba(244,239,228,0.62); cursor: pointer;
                    transition: color .18s ease, background .18s ease, border-color .18s ease;
                }
                .cnav-item:hover { color: #f4efe4; background: rgba(244,239,228,0.06); }
                .cnav-item[data-active="true"] {
                    color: #f4efe4; font-weight: 700;
                    border-left-color: #f4efe4; background: rgba(244,239,228,0.08);
                }

                .cnav-bottom {
                    padding: 16px 22px 24px;
                    border-top: 1px solid rgba(244,239,228,0.12);
                    display: flex; flex-direction: column; gap: 14px;
                }
                .cnav-lang { display: flex; align-items: center; gap: 8px; }
                .cnav-lang button {
                    background: none; border: none; padding: 2px 0; cursor: pointer;
                    font-family: ${FONT}; font-size: 11px; color: #f4efe4;
                }
                .cnav-lang span { opacity: 0.3; font-size: 11px; }

                .cnav-user { display: flex; align-items: center; gap: 10px; cursor: pointer; }
                .cnav-avatar {
                    width: 30px; height: 30px; flex-shrink: 0; overflow: hidden;
                    border-radius: 50%;
                    background: rgba(244,239,228,0.12); color: #f4efe4;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 10px; font-weight: 700; font-family: ${FONT};
                }
                .cnav-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
                .cnav-username { font-size: 12px; font-weight: 500; color: rgba(244,239,228,0.72); font-family: ${FONT}; }

                .cnav-logout {
                    background: none; border: none; padding: 0; text-align: left; cursor: pointer;
                    font-family: ${FONT}; font-size: 12px; font-weight: 700; color: #d98f85;
                }
                .cnav-logout:hover { color: #f0a89e; }

                @media (max-width: ${CREATOR_BREAKPOINT}px) {
                    .cnav-topbar {
                        display: flex; align-items: center; justify-content: space-between;
                        position: fixed; top: 0; left: 0; right: 0; height: 60px;
                        background: #0e1013; padding: 0 18px; z-index: 250; box-sizing: border-box;
                    }
                    .cnav-topbar img { height: 22px; width: auto; display: block; cursor: pointer; }
                    .cnav-hamburger {
                        background: none; border: none; padding: 8px; margin: -8px;
                        display: flex; flex-direction: column; gap: 4px; cursor: pointer;
                    }
                    .cnav-hamburger span { display: block; width: 20px; height: 1.5px; background: #f4efe4; }

                    .cnav-sidebar {
                        width: min(280px, 84vw);
                        transform: translateX(-100%);
                        transition: transform .3s cubic-bezier(.4,0,.2,1);
                    }
                    .cnav-sidebar.cnav-open { transform: translateX(0); }

                    .cnav-backdrop {
                        display: block; position: fixed; inset: 0;
                        background: rgba(0,0,0,0.5); z-index: 290;
                        opacity: 0; pointer-events: none; transition: opacity .3s ease;
                    }
                    .cnav-backdrop.cnav-open { opacity: 1; pointer-events: auto; }

                    .cnav-close {
                        display: block; background: none; border: none; padding: 4px; margin-right: 14px;
                        cursor: pointer; color: #f4efe4; font-size: 20px; line-height: 1;
                    }
                }
            `}</style>

            <div className="cnav-topbar">
                <img src="/winston-smiley-white.svg" alt="winston.ie" onClick={() => go('/')} />
                <button className="cnav-hamburger" aria-label="Menu" onClick={() => setOpen(true)}>
                    <span /><span /><span />
                </button>
            </div>

            <div className={`cnav-backdrop ${open ? 'cnav-open' : ''}`} onClick={() => setOpen(false)} />

            <div className={`cnav-sidebar ${open ? 'cnav-open' : ''}`}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div className="cnav-logo" onClick={() => go('/')}>
                            <img src="/winston-smiley-white.svg" alt="winston.ie" />
                            <span>winston.ie</span>
                        </div>
                        <button className="cnav-close" aria-label="Close" onClick={() => setOpen(false)}>✕</button>
                    </div>

                    <div className="cnav-items">
                        {NAV_ITEMS.map((item) => (
                            <button
                                key={item.path}
                                className="cnav-item"
                                data-active={activePath === item.path}
                                onClick={() => go(item.path)}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="cnav-bottom">
                    <div className="cnav-lang" role="group" aria-label="Langue / Language">
                        <button style={{ fontWeight: lang === 'fr' ? 700 : 500, opacity: lang === 'fr' ? 1 : 0.45 }} onClick={() => setLang('fr')}>FR</button>
                        <span>/</span>
                        <button style={{ fontWeight: lang === 'en' ? 700 : 500, opacity: lang === 'en' ? 1 : 0.45 }} onClick={() => setLang('en')}>EN</button>
                    </div>

                    <div className="cnav-user" onClick={() => go('/admin/settings')} title={username}>
                        <div className="cnav-avatar">
                            {avatarUrl ? <img src={avatarUrl} alt={username} /> : initials}
                        </div>
                        <span className="cnav-username">@{username}</span>
                    </div>

                    <button className="cnav-logout" onClick={onLogout}>{t('logout')}</button>
                </div>
            </div>
        </>
    );
}